import { h } from 'preact';
import { useState, useEffect } from 'preact/compat';
import styled from 'react-emotion';

const ZoomImageWrapper = styled('div')`
  position: absolute;
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  border-radius: ${props => props.radius}px;
  ${props => (props.zIndex ? `z-index:${props.zIndex}` : '')};
  ${props => (props.renderToContainer ? '' : 'overflow: hidden;')};
`;

const Image = styled('img')`
  position: absolute;
  translate: -50% -50%;
  inset: 50%;
  opacity: 0;
`;

function normalizeOverflow(value, threshold) {
  if (threshold <= 0) {
    // image is not overflowing
    return 0;
  } else if (value < -threshold) {
    return -threshold;
  } else if (value > threshold) {
    return threshold;
  }

  return value;
}

const ZoomImage = props => {
  const [container, setContainer] = useState({});
  const [rendered, setRendered] = useState({});
  const [overflow, setOverflow] = useState({});

  useEffect(() => {
    // Calculate image overflow
    const overflowX = (rendered.x * props.level - container.x) / 2;
    const overflowY = (rendered.y * props.level - container.y) / 2;
    setOverflow({ x: overflowX, y: overflowY });
  }, [container, rendered, props.level]);

  const onLoaded = e => {
    const img = e.target;
    if (!img || !img.offsetParent) return;

    const container = img.offsetParent;

    const aspectRatioImg = img.naturalWidth / img.naturalHeight;
    const aspectRatioContainer = container.offsetWidth / container.offsetHeight;

    let renderedWidth;
    let renderedHeight;

    // If the aspect ratio of the container is greater than the aspect ratio of the image
    if (aspectRatioContainer > aspectRatioImg) {
      // Calculate the rendered width by multiplying the offset height of the container by the aspect ratio of the image
      renderedWidth = container.offsetHeight * aspectRatioImg;
      // The rendered height is the offset height of the container
      renderedHeight = container.offsetHeight;
    } else {
      // The rendered width is the offset width of the container
      renderedWidth = container.offsetWidth;
      // Calculate the rendered height by dividing the offset width of the container by the aspect ratio of the image
      renderedHeight = container.offsetWidth / aspectRatioImg;
    }

    setContainer({ x: container.offsetWidth, y: container.offsetHeight });
    setRendered({ x: renderedWidth, y: renderedHeight });
  };

  const wrapperStyles = {
    backgroundColor: props.zoomed ? props.bgColor : 'transparent',
    left: `${props.left}px`,
    top: `${props.top}px`,
    transition: `background-color 150ms`
  };

  const imgStyles = {
    // Small img get stretched while bigger img loads
    width: `${props.width}px`,
    height: `${props.height}px`,
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'contain', // maintain aspect ratio

    ...(props.transition && {
      transition: 'all 150ms'
    }),

    ...(props.zoomed && {
      // when zoomed
      width: `${props.width * props.level}px`,
      height: `${props.height * props.level}px`,
      opacity: 1,
      translate: `
        calc(-50% - ${normalizeOverflow(props.lensLeft * props.level, overflow.x)}px)
        calc(-50% - ${normalizeOverflow(props.lensTop * props.level, overflow.y)}px)
      `
    })
  };

  return (
    <ZoomImageWrapper
      data-test="zoom-flyout-wrap"
      width={props.width}
      height={props.height}
      level={props.level}
      radius={props.radius}
      zIndex={props.zIndex}
      spacing={props.spacing}
      renderToContainer={props.renderToContainer}
      style={wrapperStyles}
    >
      <Image
        itemprop="image"
        src={props.url}
        style={imgStyles}
        alt={props.alt}
        level={props.level}
        onLoad={onLoaded}
      />
    </ZoomImageWrapper>
  );
};

export default ZoomImage;
